<div class="chat-composer border-top px-2 pt-2 pb-1">
  <!-- Offline chip — commands queue in ChatSocketService and flush on reconnect -->
  @if (offline) {
    <div class="pb-1">
      <span
        class="badge bg-soft-warning text-warning d-inline-flex align-items-center gap-1 text-wrap text-start"
      >
        <i class="ri-wifi-off-line"></i>
        <span>{{ "chat.composer.offlineQueued" | translate }}</span>
      </span>
    </div>
  }

  <!-- Formatting toolbar (markdown subset — Aa toggle below) -->
  @if (toolbarVisible) {
    <div
      class="btn-toolbar gap-1 pb-1"
      role="toolbar"
      [attr.aria-label]="'chat.composer.toolbarToggle' | translate"
    >
      <div class="btn-group btn-group-sm" role="group">
        <button
          type="button"
          class="btn btn-light"
          (mousedown)="$event.preventDefault()"
          (click)="formatBold()"
          [disabled]="disabled"
          [matTooltip]="'chat.composer.bold' | translate"
          [attr.aria-label]="'chat.composer.bold' | translate"
        >
          <i class="ri-bold"></i>
        </button>
        <button
          type="button"
          class="btn btn-light"
          (mousedown)="$event.preventDefault()"
          (click)="formatItalic()"
          [disabled]="disabled"
          [matTooltip]="'chat.composer.italic' | translate"
          [attr.aria-label]="'chat.composer.italic' | translate"
        >
          <i class="ri-italic"></i>
        </button>
        <button
          type="button"
          class="btn btn-light"
          (mousedown)="$event.preventDefault()"
          (click)="formatUnderline()"
          [disabled]="disabled"
          [matTooltip]="'chat.composer.underline' | translate"
          [attr.aria-label]="'chat.composer.underline' | translate"
        >
          <i class="ri-underline"></i>
        </button>
        <button
          type="button"
          class="btn btn-light"
          (mousedown)="$event.preventDefault()"
          (click)="formatStrike()"
          [disabled]="disabled"
          [matTooltip]="'chat.composer.strike' | translate"
          [attr.aria-label]="'chat.composer.strike' | translate"
        >
          <i class="ri-strikethrough"></i>
        </button>
      </div>
      <div class="btn-group btn-group-sm" role="group">
        <button
          type="button"
          class="btn btn-light"
          (mousedown)="$event.preventDefault()"
          (click)="formatLink()"
          [disabled]="disabled"
          [matTooltip]="'chat.composer.link' | translate"
          [attr.aria-label]="'chat.composer.link' | translate"
        >
          <i class="ri-link"></i>
        </button>
        <button
          type="button"
          class="btn btn-light"
          (mousedown)="$event.preventDefault()"
          (click)="formatOrderedList()"
          [disabled]="disabled"
          [matTooltip]="'chat.composer.orderedList' | translate"
          [attr.aria-label]="'chat.composer.orderedList' | translate"
        >
          <i class="ri-list-ordered"></i>
        </button>
        <button
          type="button"
          class="btn btn-light"
          (mousedown)="$event.preventDefault()"
          (click)="formatUnorderedList()"
          [disabled]="disabled"
          [matTooltip]="'chat.composer.unorderedList' | translate"
          [attr.aria-label]="'chat.composer.unorderedList' | translate"
        >
          <i class="ri-list-unordered"></i>
        </button>
      </div>
      <div class="btn-group btn-group-sm" role="group">
        <button
          type="button"
          class="btn btn-light"
          (mousedown)="$event.preventDefault()"
          (click)="formatInlineCode()"
          [disabled]="disabled"
          [matTooltip]="'chat.composer.inlineCode' | translate"
          [attr.aria-label]="'chat.composer.inlineCode' | translate"
        >
          <i class="ri-code-line"></i>
        </button>
        <button
          type="button"
          class="btn btn-light"
          (mousedown)="$event.preventDefault()"
          (click)="formatCodeBlock()"
          [disabled]="disabled"
          [matTooltip]="'chat.composer.codeBlock' | translate"
          [attr.aria-label]="'chat.composer.codeBlock' | translate"
        >
          <i class="ri-code-box-line"></i>
        </button>
        <button
          type="button"
          class="btn btn-light"
          (mousedown)="$event.preventDefault()"
          (click)="insertMentionTrigger()"
          [disabled]="disabled"
          [matTooltip]="'chat.composer.mention' | translate"
          [attr.aria-label]="'chat.composer.mention' | translate"
        >
          <i class="ri-at-line"></i>
        </button>
      </div>
    </div>
  }

  <!-- Pending-attachments tray (T-33): thumbnail/icon · name · size · progress · cancel -->
  @if (attachments.length > 0) {
    <div class="chat-attach-tray d-flex flex-wrap gap-1 pb-1">
      @for (attachment of attachments; track attachment.id) {
        <div
          class="chat-attach-chip border rounded d-flex align-items-center gap-2 px-1 py-1"
          [class.border-danger]="attachment.status === 'error'"
        >
          @if (attachment.previewUrl) {
            <img
              class="chat-attach-chip__thumb rounded flex-shrink-0"
              [src]="attachment.previewUrl"
              [alt]="attachment.name"
            />
          } @else {
            <span class="avatar-xs rounded bg-soft-primary flex-shrink-0">
              <i
                class="avatar-title text-primary font-16"
                [ngClass]="attachment.icon"
              ></i>
            </span>
          }
          <div class="chat-attach-chip__meta flex-grow-1 overflow-hidden">
            <small class="d-block text-truncate fw-semibold">{{
              attachment.name
            }}</small>
            @if (attachment.status === "error") {
              <small class="text-danger d-block">{{
                "chat.composer.attachmentFailed" | translate
              }}</small>
            } @else {
              <small class="text-muted d-block">{{
                attachment.sizeLabel
              }}</small>
            }
            @if (attachment.status === "uploading") {
              <div
                class="progress chat-attach-chip__progress mt-1"
                role="progressbar"
                [attr.aria-valuenow]="attachment.progress"
                aria-valuemin="0"
                aria-valuemax="100"
              >
                <div
                  class="progress-bar"
                  [style.width.%]="attachment.progress"
                ></div>
              </div>
            }
          </div>
          <button
            type="button"
            class="btn btn-sm btn-light chat-attach-chip__remove flex-shrink-0"
            (click)="removeAttachment(attachment)"
            [matTooltip]="
              (attachment.status === 'uploading'
                ? 'chat.composer.cancelUpload'
                : 'chat.composer.removeAttachment'
              ) | translate
            "
            [attr.aria-label]="
              (attachment.status === 'uploading'
                ? 'chat.composer.cancelUpload'
                : 'chat.composer.removeAttachment'
              ) | translate
            "
          >
            <i class="ri-close-line"></i>
          </button>
        </div>
      }
    </div>
  }

  <!-- Pending work reference (T-22 picker · RF-35): rides the next message -->
  @if (pendingContextRef) {
    <div class="pb-1">
      <chat-context-ref-card
        [reference]="pendingContextRef"
        [removable]="true"
        (remove)="clearContextRef()"
      ></chat-context-ref-card>
    </div>
  }

  <!-- Recorder mode (T-34): pulse dot · mm:ss · live level hint · discard · send -->
  @if (recording) {
    <div
      class="chat-recorder d-flex align-items-center gap-2 py-1"
      role="status"
    >
      <span
        class="chat-recorder__dot bg-danger rounded-circle flex-shrink-0"
      ></span>
      <span class="fw-semibold chat-recorder__time flex-shrink-0">{{
        recordingLabel
      }}</span>
      <span
        class="chat-recorder__level-track flex-grow-1 rounded overflow-hidden"
      >
        <span
          #recorderLevel
          class="chat-recorder__level d-block bg-danger rounded"
        ></span>
      </span>
      <small class="text-muted d-none d-sm-inline flex-shrink-0">{{
        "chat.composer.recording" | translate
      }}</small>
      <button
        type="button"
        class="btn btn-sm btn-light flex-shrink-0"
        (click)="cancelRecording()"
        [matTooltip]="'chat.composer.recordingDiscard' | translate"
        [attr.aria-label]="'chat.composer.recordingDiscard' | translate"
      >
        <i class="ri-delete-bin-line"></i>
      </button>
      <button
        type="button"
        class="btn btn-sm btn-primary flex-shrink-0"
        (click)="sendRecording()"
        [matTooltip]="'chat.composer.recordingSend' | translate"
        [attr.aria-label]="'chat.composer.recordingSend' | translate"
      >
        <i class="ri-send-plane-2-fill"></i>
      </button>
    </div>
  } @else {
    <form class="d-flex align-items-end gap-2" (ngSubmit)="submit()">
      <div class="flex-grow-1 position-relative">
        <!-- Mention autocomplete (Slack-style, keyboard navigable) -->
        @if (mentionOpen) {
          <div class="chat-mention-menu card shadow-sm mb-1" role="listbox">
            <div class="list-group list-group-flush chat-mention-menu__list">
              @for (option of mentionOptions; track option.id; let i = $index) {
                <button
                  type="button"
                  class="list-group-item list-group-item-action d-flex align-items-center gap-2 py-1 px-2"
                  role="option"
                  [attr.aria-selected]="i === mentionActiveIndex"
                  [class.active]="i === mentionActiveIndex"
                  (mousedown)="$event.preventDefault()"
                  (click)="selectMention(option)"
                >
                  @if (option.image) {
                    <img
                      class="avatar-xs rounded-circle flex-shrink-0"
                      [src]="option.image"
                      [alt]="option.name"
                    />
                  } @else {
                    <span
                      class="avatar-xs rounded-circle bg-soft-primary flex-shrink-0"
                    >
                      <span class="avatar-title text-primary">{{
                        (option.name || "?").charAt(0).toUpperCase()
                      }}</span>
                    </span>
                  }
                  <span class="chat-name">{{ option.name }}</span>
                </button>
              }
            </div>
          </div>
        }

        <textarea
          #composerInput
          name="chatComposerDraft"
          class="form-control chat-composer__input"
          rows="1"
          [placeholder]="'chat.composer.placeholder' | translate"
          [attr.aria-label]="'chat.composer.placeholder' | translate"
          [attr.maxlength]="maxLength"
          [(ngModel)]="draft"
          [disabled]="disabled"
          (keydown)="onKeydown($event)"
          (keyup)="onCaretMove()"
          (click)="onCaretMove()"
          (input)="onInput()"
          (paste)="onPaste($event)"
          (blur)="onBlur()"
        ></textarea>
      </div>
      <button
        type="submit"
        class="btn btn-primary chat-composer__send flex-shrink-0"
        [disabled]="disabled || !canSend"
        [attr.aria-label]="'chat.composer.send' | translate"
        [matTooltip]="'chat.composer.send' | translate"
      >
        <i class="ri-send-plane-2-fill"></i>
      </button>
    </form>
  }

  <!-- Bottom row: Aa toggle · emoji · hint · char counter -->
  <div class="d-flex align-items-center gap-1 pt-1">
    <button
      type="button"
      class="btn btn-sm chat-composer__aa chat-composer__tool fw-semibold"
      [class.btn-soft-primary]="toolbarVisible"
      (click)="toggleToolbar()"
      [matTooltip]="'chat.composer.toolbarToggle' | translate"
      [attr.aria-label]="'chat.composer.toolbarToggle' | translate"
      [attr.aria-pressed]="toolbarVisible"
    >
      <i class="ri-font-size-2"></i>
    </button>

    <!-- Paperclip (T-33): opens the multi-file picker; limits run pre-upload -->
    <input
      #fileInput
      type="file"
      class="d-none"
      multiple
      (change)="onFileInputChange($event)"
    />
    <button
      type="button"
      class="btn btn-sm chat-composer__tool"
      [disabled]="disabled"
      (click)="openFilePicker()"
      [matTooltip]="'chat.composer.attach' | translate"
      [attr.aria-label]="'chat.composer.attach' | translate"
    >
      <i class="ri-attachment-2"></i>
    </button>

    <!-- Work reference (T-22/T-24 · RF-35): attendance / card / contact picker -->
    <button
      type="button"
      class="btn btn-sm chat-composer__tool"
      [class.btn-soft-primary]="pendingContextRef"
      [disabled]="disabled"
      (click)="attachContextRef()"
      [matTooltip]="'chat.contextRef.attach' | translate"
      [attr.aria-label]="'chat.contextRef.attach' | translate"
    >
      <i class="ri-links-line"></i>
    </button>

    <!-- Mic (T-34): flips the composer into recording mode -->
    <button
      type="button"
      class="btn btn-sm chat-composer__tool"
      [disabled]="disabled || recording"
      (click)="startRecording()"
      [matTooltip]="'chat.composer.record' | translate"
      [attr.aria-label]="'chat.composer.record' | translate"
    >
      <i class="ri-mic-line"></i>
    </button>

    <div class="dropup d-inline-block">
      <button
        type="button"
        class="btn btn-sm chat-composer__tool dropdown-toggle arrow-none"
        data-bs-toggle="dropdown"
        data-bs-offset="-24,2"
        aria-expanded="false"
        [disabled]="disabled"
        [matTooltip]="'chat.composer.emoji' | translate"
        [attr.aria-label]="'chat.composer.emoji' | translate"
      >
        <i class="ri-emotion-line"></i>
      </button>
      <div class="dropdown-menu chat-emoji-menu p-2">
        @for (emoji of emojis; track emoji) {
          <button
            type="button"
            class="btn btn-sm btn-light chat-emoji-menu__item"
            (mousedown)="$event.preventDefault()"
            (click)="insertEmoji(emoji)"
          >
            {{ emoji }}
          </button>
        }
      </div>
    </div>

    <small class="chat-composer__hint flex-grow-1 ms-1 text-truncate"
      >{{ "chat.composer.hint" | translate
      }}@if (editLast.observed) {
        · {{ "chat.composer.editLastHint" | translate }}
      }</small
    >

    @if (counterVisible) {
      <small
        class="flex-shrink-0"
        [class.text-warning]="!atLimit"
        [class.text-danger]="atLimit"
      >
        {{ draft.length }}/{{ maxLength }}
      </small>
    }
  </div>

  @if (atLimit) {
    <small class="text-danger d-block">{{
      "chat.composer.charLimitReached" | translate: { max: maxLength }
    }}</small>
  }
</div>
